import {
  AccountBalanceOutlined,
  AddOutlined,
  BoltOutlined,
  BusinessOutlined,
  ChevronRight as ChevronRightIcon,
  EditOutlined,
  ExpandMore as ExpandMoreIcon,
  HandshakeOutlined,
  HistoryOutlined,
  LockOutlined,
  PaidOutlined,
  PeopleOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { amber, blue, green, grey } from "@mui/material/colors";
import { isEmpty, isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import Flx from "../../../../components/layout/Flx";
import ScreenContent from "../../../../components/layout/ScreenContent";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useFundingStatementsHook } from "../../../../hooks/useFundingStatementsHook";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import {
  sidebarSetParamsValue,
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { loanFetchedDataLoanTermsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import {
  getEscrowAmountCollected,
  getEscrowMonthsCollected,
} from "../../../../utils/loan/escrowCollection";
import { isDscrProduct } from "../../../../utils/loan/productType";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";

const sumAmounts = (items) =>
  (items || []).reduce((acc, x) => acc + (Number(x?.totalDueAmount) || 0), 0);

const sumLineAmounts = (lines, key = "amount") =>
  (lines || []).reduce((acc, x) => acc + (Number(x?.[key]) || 0), 0);

// Escrows are collected at closing on DSCR loans only — the backend computes
// Escrow Items for DSCR and nothing else (closing.from_underwriting). Items on
// other programs (e.g. left behind by the retired tax-bill writeback) are shown
// but never counted.
const collectsEscrow = (loanProductType) =>
  isDscrProduct(String(loanProductType || "").trim());

// Since 2026-09-24 the holdback and financed interest reserve come back as
// their own `holdbackReserveLines`, not as fees. A fee item that is really one
// of those (a manually-added "Rehab Escrow", say) would count the same money
// twice, so it is left out of every total here — the same words the backend's
// Net Wire Calculator drops (funding_statements.build_net_wire_calculator).
const HOLDBACK_FEE_WORDS = ["rehab escrow", "construction escrow", "interest reserve"];
const isHoldbackReserveFee = (item) => {
  const text = `${item?.itemName || ""} ${item?.feeCategory || ""}`.toLowerCase();
  return HOLDBACK_FEE_WORDS.some((w) => text.includes(w));
};

// Holdback / financed reserve lines withheld from the wire at closing.
const isWithheldLine = (line) => line?.paidFrom === "Net Funded";

// Auto-calculated fees carry a raw `calculationBasis` slug (e.g.
// "per_diem_interest") that isn't worth surfacing. Prefer the human-readable
// `itemNote` the backend writes — the per-diem calc string ("N days x $X x
// factor"), or a Salesforce-synced note — and fall back to the effective percent
// for points-based fees (Discount/Origination Points) that have no note.
const autoCalcDetail = (f) => {
  if (f?.itemNote) return f.itemNote;
  const pct = Number(f?.effectivePercent);
  if (pct > 0) return `${formatPercent(pct)} of loan amount`;
  return null;
};

// Correspondent fees that EV *charges* the correspondent (shown as a negative
// in the Net Wire to Correspondent calc). Everything else in the correspondent
// section is treated as paid out to the correspondent. There is no structured
// "direction" flag on the fee model yet, so this is matched by fee category /
// name — revisit once the backend adds a per-fee chargedToCorrespondent flag.
const EV_CHARGED_CORRESPONDENT_CATEGORIES = [
  "EV Underwriting Fee",
  "EV Origination Fee",
  "Table Funding Fee",
];
const EV_CHARGED_LOOKUP = new Set(
  EV_CHARGED_CORRESPONDENT_CATEGORIES.map((s) => s.toLowerCase()),
);
const isEvChargedCorrespondentFee = (item) => {
  const cat = String(item?.feeCategory || "").trim().toLowerCase();
  const name = String(item?.itemName || "").trim().toLowerCase();
  return EV_CHARGED_LOOKUP.has(cat) || EV_CHARGED_LOOKUP.has(name);
};

const LoanDrilldownFeesRedesignTab = () => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const { getLoanTerms } = useLoanTermsHook();
  const { getNetWire } = useFundingStatementsHook();

  // The backend Net Wire Calculator is the wire of record (holdback + financed
  // reserve withheld, escrows DSCR-only). `undefined` = loading, `null` = failed.
  const [netWire, setNetWire] = useState(undefined);

  useEffect(() => {
    if (isNil(loanTerms) && loanDrilldown?._id) {
      getLoanTerms({
        dealId: loanDrilldown?._id,
        onSuccessFn: (data) => dispatch(loanFetchedDataLoanTermsSet(data)),
        onFailFn: () => dispatch(loanFetchedDataLoanTermsSet({})),
      });
    }
  }, [loanTerms, loanDrilldown?._id]);

  // Re-read whenever the terms change (every fee edit replaces the loanTerms
  // object) or the loan itself is saved (amounts, holdback, closing date).
  useEffect(() => {
    if (!loanDrilldown?._id || isNil(loanTerms)) return;
    let cancelled = false;
    getNetWire({
      dealId: loanDrilldown._id,
      onSuccessFn: (data) => !cancelled && setNetWire(data || null),
      onFailFn: () => !cancelled && setNetWire(null),
    });
    return () => {
      cancelled = true;
    };
  }, [loanDrilldown?._id, loanDrilldown?.updateDateTime, loanTerms]);

  const totals = useMemo(() => {
    const productType =
      loanTerms?.loanProductType || loanDrilldown?.loanProductType;
    const escrowCollected = collectsEscrow(productType);
    const originatorFees = loanTerms?.originatorFees || [];
    const holdbackDuplicates = originatorFees.filter(isHoldbackReserveFee);
    const originator = sumAmounts(
      originatorFees.filter((f) => !isHoldbackReserveFee(f)),
    );
    const holdbackLines = loanTerms?.holdbackReserveLines || [];
    const withheld = sumLineAmounts(
      holdbackLines.filter(isWithheldLine),
      "itemAmount",
    );
    const thirdParty = sumAmounts(loanTerms?.thirdPartyFees);
    const correspondent = sumAmounts(loanTerms?.correspondentFees);
    const broker = sumAmounts(loanTerms?.brokerTerms);
    const brokerNotInOrigination = (loanTerms?.brokerTerms || [])
      .filter((b) => !b?.includedInOriginatorFees)
      .reduce((a, x) => a + (Number(x?.totalDueAmount) || 0), 0);
    const credits = sumAmounts(loanTerms?.originatorCredits);
    const escrow = escrowCollected
      ? (loanTerms?.escrowItems || []).reduce(
          (a, x) => a + (Number(getEscrowAmountCollected(x)) || 0),
          0,
        )
      : 0;
    const totalFees = originator + thirdParty + correspondent + broker;
    const net = totalFees - credits;
    // Correspondent fees are excluded — they are flow-through (charged to the
    // borrower on the HUD, then paid to the correspondent separately), so they
    // are back-end numbers that do not belong in the borrower's cash to close.
    const netClosingFees =
      originator + brokerNotInOrigination + thirdParty - credits;
    const totalCashToClose = netClosingFees + escrow;
    const loanAmount =
      Number(loanTerms?.totalLoanAmount) ||
      Number(loanDrilldown?.totalLoanAmount) ||
      0;
    const effectivePct = loanAmount > 0 ? totalFees / loanAmount : 0;
    const qmPct = loanAmount > 0 ? netClosingFees / loanAmount : 0;
    // Local ESTIMATE of the net wire, used only until (or if) the backend Net
    // Wire Calculator can't be read: Total Loan − holdback / financed reserve
    // withheld − Lender Fees + Lender Credits − Escrows (DSCR only).
    const netWireToTitle = loanAmount - withheld - originator + credits - escrow;
    // Net wire to correspondent — fees paid out to the correspondent minus the
    // fees EV charges them (EV UW / EV Origination / Table Funding).
    const correspondentEvCharged = (loanTerms?.correspondentFees || [])
      .filter(isEvChargedCorrespondentFee)
      .reduce((a, x) => a + (Number(x?.totalDueAmount) || 0), 0);
    const correspondentPaidOut = correspondent - correspondentEvCharged;
    const netWireToCorrespondent = correspondentPaidOut - correspondentEvCharged;
    return {
      originator,
      thirdParty,
      correspondent,
      broker,
      brokerNotInOrigination,
      credits,
      escrow,
      escrowCollected,
      withheld,
      holdbackDuplicates,
      netWireToTitle,
      correspondentEvCharged,
      correspondentPaidOut,
      netWireToCorrespondent,
      totalFees,
      net,
      netClosingFees,
      totalCashToClose,
      loanAmount,
      effectivePct,
      qmPct,
    };
  }, [
    loanTerms,
    loanDrilldown?.totalLoanAmount,
    loanDrilldown?.loanProductType,
  ]);

  const headerSubtitle = useMemo(() => {
    const num = loanDrilldown?.loanNumber || "XXXXXX";
    return `Loan #${num} — Total loan amount ${formatDollar(totals.loanAmount)}`;
  }, [loanDrilldown, totals.loanAmount]);

  return (
    <>
      <LoanDrilldownTabPanel>
        <ScreenContent sx={{ width: "100%", margin: "0 auto" }}>
          <PrimaryContentCard
            maxWidth="xl"
            title="Fees Ledger"
            subTitle={headerSubtitle}
            loading={isNil(loanTerms)}
          >
            {!isNil(loanTerms) && (
              <LedgerView
                loanTerms={loanTerms}
                totals={totals}
                netWire={netWire}
              />
            )}
          </PrimaryContentCard>
        </ScreenContent>
      </LoanDrilldownTabPanel>
    </>
  );
};

/* ═══════════════════════════════════════════
   LEDGER VIEW — HUD-style
   ═══════════════════════════════════════════ */

const LedgerView = ({ loanTerms, totals, netWire }) => {
  return (
    <Flx column g={3}>
      <LedgerSummaryStrip totals={totals} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 2fr) minmax(0, 1fr)",
          },
          gap: 3,
        }}
      >
        <LedgerMainColumn loanTerms={loanTerms} totals={totals} />
        <LedgerRightSidebar
          totals={totals}
          loanTerms={loanTerms}
          netWire={netWire}
        />
      </Box>
    </Flx>
  );
};

/* ─── Ledger summary strip (6 boxes) ─── */

const LedgerSummaryStrip = ({ totals }) => {
  const boxes = [
    {
      label: "Lender",
      value: formatDollar(totals.originator),
      tone: "light",
    },
    {
      label: "Correspondent",
      value: formatDollar(totals.correspondent),
      tone: "light",
    },
    {
      label: "Third-Party",
      value: formatDollar(totals.thirdParty),
      tone: "light",
    },
    {
      label: "Escrow Collections",
      value: formatDollar(totals.escrow),
      tone: "light",
    },
    {
      label: "Credits",
      value: `-${formatDollar(totals.credits)}`,
      tone: "emerald",
    },
    {
      label: "Net Cash to Close",
      value: formatDollar(totals.totalCashToClose),
      tone: "dark",
    },
  ];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
          lg: "repeat(6, minmax(0, 1fr))",
        },
        gap: 1.5,
      }}
    >
      {boxes.map((b) => (
        <LedgerSummaryBox key={b.label} {...b} />
      ))}
    </Box>
  );
};

const LedgerSummaryBox = ({ label, value, tone }) => {
  const palettes = {
    light: {
      bg: "#ffffff",
      border: grey[300],
      labelColor: grey[500],
      valueColor: "#0f172a",
    },
    emerald: {
      bg: green[50],
      border: green[100],
      labelColor: green[700],
      valueColor: green[800],
    },
    dark: {
      bg: "#0f172a",
      border: "#0f172a",
      labelColor: grey[400],
      valueColor: "#ffffff",
    },
  };
  const c = palettes[tone] || palettes.light;
  return (
    <Flx
      column
      g={0.5}
      sx={{
        borderRadius: 2,
        border: `1px solid ${c.border}`,
        background: c.bg,
        px: 2,
        py: 1.5,
      }}
    >
      <Txt
        sx={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.6,
          color: c.labelColor,
        }}
      >
        {String(label).toUpperCase()}
      </Txt>
      <Txt
        sx={{
          fontSize: 18,
          fontWeight: 600,
          color: c.valueColor,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Txt>
    </Flx>
  );
};

/* ─── Ledger main column: fee sections w/ typed rows ─── */

const LEDGER_SECTIONS = [
  {
    key: "originatorFees",
    title: "Lender Fees",
    subtitle: "Fees charged by the lender including points and per diem",
    icon: <BusinessOutlined />,
    sidebarType: loanDrilldownSidebarTypes.ADD_LOAN_FEE,
    RowComponent: LedgerOriginatorRow,
    totalsKey: "originator",
  },
  {
    key: "originatorCredits",
    title: "Lender Credits",
    subtitle: "Credits that reduce borrower costs",
    icon: <PaidOutlined sx={{ color: green[600] }} />,
    sidebarType: loanDrilldownSidebarTypes.ADD_LOAN_CREDIT,
    RowComponent: LedgerCreditRow,
    totalsKey: "credits",
    isCredit: true,
  },
  {
    key: "correspondentFees",
    title: "Correspondent Fees",
    subtitle: "Fees assessed by correspondent partners",
    icon: <PeopleOutlined />,
    sidebarType: loanDrilldownSidebarTypes.ADD_EDIT_CORRESPONDENT_FEE,
    RowComponent: LedgerCorrespondentRow,
    totalsKey: "correspondent",
  },
  {
    key: "brokerTerms",
    title: "Broker Terms",
    subtitle: "Broker payouts and compensation",
    icon: <HandshakeOutlined />,
    sidebarType: loanDrilldownSidebarTypes.ADD_EDIT_BROKER_TERMS,
    RowComponent: LedgerBrokerRow,
    totalsKey: "broker",
  },
  {
    key: "thirdPartyFees",
    title: "Third Party Fees & Taxes and Insurance Due at Closing",
    subtitle:
      "Vendor fees, plus open property taxes and insurance premiums paid through the settlement",
    icon: <ReceiptLongOutlined />,
    sidebarType: loanDrilldownSidebarTypes.ADD_EDIT_THIRD_PARTY_FEES,
    RowComponent: LedgerThirdPartyRow,
    totalsKey: "thirdParty",
  },
  {
    key: "escrowItems",
    title: "Escrow Items",
    subtitle: "Collected at closing for future disbursement",
    icon: <LockOutlined />,
    sidebarType: loanDrilldownSidebarTypes.ADD_LOAN_ESCROW_ITEM,
    RowComponent: LedgerEscrowRow,
    totalsKey: "escrow",
  },
];

const LedgerMainColumn = ({ loanTerms, totals }) => {
  const [expanded, setExpanded] = useState(() => ({
    originatorFees: true,
    holdbackReserveLines: true,
    originatorCredits: false,
    correspondentFees: false,
    brokerTerms: false,
    thirdPartyFees: false,
    escrowItems: false,
  }));

  const toggle = useCallback(
    (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] })),
    [],
  );

  const escrowNote =
    !totals.escrowCollected && !isEmpty(loanTerms?.escrowItems)
      ? "Escrows are only collected on DSCR loans, so these items are not counted in cash to close or the wire. Remove them, and enter taxes or insurance due at closing under Third Party Fees."
      : null;

  return (
    <Flx column g={1.5}>
      {LEDGER_SECTIONS.map((s) => (
        <Box key={s.key} sx={{ display: "contents" }}>
          <LedgerFeeSection
            title={s.title}
            subtitle={s.subtitle}
            icon={s.icon}
            isExpanded={!!expanded[s.key]}
            onToggle={() => toggle(s.key)}
            items={loanTerms?.[s.key]}
            total={totals[s.totalsKey]}
            sidebarType={s.sidebarType}
            RowComponent={s.RowComponent}
            rowProps={
              s.key === "escrowItems"
                ? { notCollected: !totals.escrowCollected }
                : undefined
            }
            note={s.key === "escrowItems" ? escrowNote : null}
            isCredit={!!s.isCredit}
          />
          {s.key === "originatorFees" && (
            <HoldbackReserveSection
              lines={loanTerms?.holdbackReserveLines}
              isExpanded={!!expanded.holdbackReserveLines}
              onToggle={() => toggle("holdbackReserveLines")}
            />
          )}
        </Box>
      ))}
    </Flx>
  );
};

// Shared header for every ledger section (fee sections + the read-only
// Holdback & Reserves block).
const LedgerSectionHeader = ({
  title,
  subtitle,
  icon,
  isExpanded,
  onToggle,
  count,
  total,
  isCredit,
}) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2.5,
      py: 1.5,
      cursor: "pointer",
      userSelect: "none",
      transition: "background-color 0.1s",
      "&:hover": { background: grey[50] },
    }}
  >
    <Flx ac g={1.5}>
      {isExpanded ? (
        <ExpandMoreIcon sx={{ color: grey[400], fontSize: 18 }} />
      ) : (
        <ChevronRightIcon sx={{ color: grey[400], fontSize: 18 }} />
      )}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          background: grey[100],
          color: grey[700],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& svg": { fontSize: 16 },
        }}
      >
        {icon}
      </Box>
      <Flx column g={0.25}>
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
          {title}
        </Txt>
        <Txt sx={{ fontSize: 11, color: grey[500] }}>{subtitle}</Txt>
      </Flx>
    </Flx>
    <Flx ac g={1.5}>
      <Txt sx={{ fontSize: 11, color: grey[500] }}>
        {count} {count === 1 ? "item" : "items"}
      </Txt>
      <Txt
        sx={{
          fontSize: 15,
          fontWeight: 600,
          color: isCredit ? green[600] : "#0f172a",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatDollar(total)}
      </Txt>
    </Flx>
  </Box>
);

const SectionNote = ({ children }) => (
  <Box
    sx={{
      px: 2.5,
      py: 1,
      background: amber[50],
      borderBottom: `1px solid ${amber[100]}`,
    }}
  >
    <Txt sx={{ fontSize: 11.5, color: amber[900] }}>{children}</Txt>
  </Box>
);

/* ─── Holdback & Reserves: derived by the backend, read-only ─── */

const HOLDBACK_PAID_FROM_LABEL = {
  "Net Funded": "Withheld from the wire",
  "Borrower Cash": "Borrower cash",
};

const HoldbackReserveSection = ({ lines, isExpanded, onToggle }) => {
  const rows = lines || [];
  if (rows.length === 0) return null;
  return (
    <Box
      sx={{
        background: "#ffffff",
        border: `1px solid ${grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <LedgerSectionHeader
        title="Holdback & Reserves"
        subtitle="From the loan's holdback and interest reserves. Not a fee, and not part of cash to close"
        icon={<AccountBalanceOutlined />}
        isExpanded={isExpanded}
        onToggle={onToggle}
        count={rows.length}
        total={sumLineAmounts(rows, "itemAmount")}
      />
      {isExpanded && (
        <Box sx={{ borderTop: `1px solid ${grey[100]}` }}>
          {rows.map((line, idx) => (
            <Flx
              key={`${line?.lineType || "line"}-${idx}`}
              ai="flex-start"
              sx={{
                gap: 2,
                px: 2.5,
                py: 1.25,
                borderBottom: `1px solid ${grey[100]}`,
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <Flx column sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
                <Flx ac g={0.75} wrap>
                  <Txt sx={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
                    {line?.itemName}
                  </Txt>
                  <AutoBadge />
                </Flx>
                <Txt sx={{ fontSize: 11, color: grey[500] }}>
                  {HOLDBACK_PAID_FROM_LABEL[line?.paidFrom] || line?.paidFrom}
                </Txt>
              </Flx>
              <Txt
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0f172a",
                  fontVariantNumeric: "tabular-nums",
                  flexShrink: 0,
                }}
              >
                {formatDollar(line?.itemAmount)}
              </Txt>
            </Flx>
          ))}
          <Box
            sx={{
              px: 2.5,
              py: 1,
              borderTop: `1px solid ${grey[100]}`,
              background: "#fafbfc",
            }}
          >
            <Txt sx={{ fontSize: 11, color: grey[500] }}>
              Calculated from the loan. To change it, edit the holdback or
              interest reserve on the loan.
            </Txt>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const LedgerFeeSection = ({
  title,
  subtitle,
  icon,
  isExpanded,
  onToggle,
  items,
  total,
  sidebarType,
  RowComponent,
  rowProps,
  note,
  isCredit,
}) => {
  const dispatch = useDispatch();
  const rows = useMemo(() => items || [], [items]);

  const onRowClicked = useCallback(
    (d) => {
      dispatch(sidebarTypeSet(sidebarType));
      dispatch(sidebarSetParamsValue(null));
      dispatch(sidebarSetStateValue(d));
    },
    [dispatch, sidebarType],
  );

  const onAddClicked = useCallback(
    (e) => {
      e.stopPropagation();
      dispatch(sidebarTypeSet(sidebarType));
      dispatch(sidebarSetParamsValue(null));
      dispatch(sidebarSetStateValue(null));
    },
    [dispatch, sidebarType],
  );

  return (
    <Box
      sx={{
        background: "#ffffff",
        border: `1px solid ${grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <LedgerSectionHeader
        title={title}
        subtitle={subtitle}
        icon={icon}
        isExpanded={isExpanded}
        onToggle={onToggle}
        count={rows.length}
        total={total ?? sumAmounts(rows)}
        isCredit={isCredit}
      />
      {isExpanded && (
        <Box sx={{ borderTop: `1px solid ${grey[100]}` }}>
          {note && <SectionNote>{note}</SectionNote>}
          {rows.map((item, idx) => (
            <Box
              key={item._id || item.oid || idx}
              onClick={() => onRowClicked(item)}
              sx={{
                px: 2.5,
                py: 1.25,
                borderBottom: `1px solid ${grey[100]}`,
                cursor: "pointer",
                "&:last-of-type": { borderBottom: "none" },
                "&:hover": {
                  background: grey[50],
                  "& .row-edit-icon": { opacity: 1 },
                },
              }}
            >
              <RowComponent item={item} {...rowProps} />
            </Box>
          ))}
          {rows.length === 0 && (
            <Box sx={{ px: 2.5, py: 2 }}>
              <Txt sx={{ fontSize: 12, color: grey[500] }}>No items yet.</Txt>
            </Box>
          )}
          <Box
            sx={{
              px: 2.5,
              py: 1,
              borderTop: `1px solid ${grey[100]}`,
              background: "#fafbfc",
            }}
          >
            <Box
              component="button"
              onClick={onAddClicked}
              sx={{
                background: "transparent",
                border: "none",
                color: grey[700],
                fontSize: 11,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                p: 0,
                "&:hover": { color: "#0f172a" },
              }}
            >
              <AddOutlined sx={{ fontSize: 13 }} />
              Add item
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

/* ─── Row components per collection type ─── */

function LedgerOriginatorRow({ item }) {
  const percent =
    item?.itemPercent == null
      ? null
      : Number(item.itemPercent) <= 1
        ? Number(item.itemPercent) * 100
        : Number(item.itemPercent);
  return (
    <Flx ai="flex-start" sx={{ gap: 2 }}>
      <Flx column sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Flx ac g={0.75} wrap>
          <Txt sx={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
            {item?.itemName}
          </Txt>
          {item?.isAutoCalculated && <AutoBadge />}
          {item?.paidOutsideClosing ? <PocChip /> : null}
          {isHoldbackReserveFee(item) && <HoldbackDuplicateChip />}
        </Flx>
        {item?.feeCategory && (
          <Txt sx={{ fontSize: 11, color: grey[500] }}>{item.feeCategory}</Txt>
        )}
        {item?.itemNote && (
          <Txt
            sx={{
              fontSize: 11,
              color: grey[400],
              fontStyle: "italic",
            }}
          >
            {item.itemNote}
          </Txt>
        )}
      </Flx>
      <Flx column sx={{ alignItems: "flex-end", flexShrink: 0 }}>
        {percent != null && (
          <Txt sx={{ fontSize: 11, color: grey[500] }}>
            {percent.toFixed(3)}%
          </Txt>
        )}
        <Txt
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#0f172a",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatDollar(item?.totalDueAmount)}
        </Txt>
      </Flx>
      <RowEditHint disabled={!!item?.isAutoCalculated} />
    </Flx>
  );
}

function LedgerCreditRow({ item }) {
  return (
    <Flx ai="flex-start" sx={{ gap: 2 }}>
      <Flx column sx={{ flex: 1, gap: 0.25 }}>
        <Txt sx={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
          {item?.itemName}
        </Txt>
        {item?.creditType && (
          <Txt
            sx={{ fontSize: 11, color: grey[500], textTransform: "capitalize" }}
          >
            {String(item.creditType).replace(/_/g, " ")}
          </Txt>
        )}
        {item?.itemNote && (
          <Txt sx={{ fontSize: 11, color: grey[400], fontStyle: "italic" }}>
            {item.itemNote}
          </Txt>
        )}
      </Flx>
      <Box sx={{ textAlign: "right" }}>
        <Txt
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: green[600],
            fontVariantNumeric: "tabular-nums",
          }}
        >
          -{formatDollar(item?.totalDueAmount)}
        </Txt>
      </Box>
      <RowEditHint />
    </Flx>
  );
}

function LedgerCorrespondentRow({ item }) {
  return (
    <Flx ai="flex-start" sx={{ gap: 2 }}>
      <Flx column sx={{ flex: 1, gap: 0.25 }}>
        <Txt sx={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
          {item?.itemName}
        </Txt>
        {item?.correspondentName && (
          <Txt sx={{ fontSize: 11, color: grey[500] }}>
            {item.correspondentName}
          </Txt>
        )}
      </Flx>
      <Box sx={{ textAlign: "right" }}>
        <Txt
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#0f172a",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatDollar(item?.totalDueAmount)}
        </Txt>
      </Box>
      <RowEditHint />
    </Flx>
  );
}

function LedgerBrokerRow({ item }) {
  const percent =
    item?.itemPercent == null
      ? null
      : Number(item.itemPercent) <= 1
        ? Number(item.itemPercent) * 100
        : Number(item.itemPercent);
  return (
    <Flx ai="flex-start" sx={{ gap: 2 }}>
      <Flx column sx={{ flex: 1, gap: 0.25 }}>
        <Flx ac g={0.75} wrap>
          <Txt sx={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
            {item?.termName || item?.itemName}
          </Txt>
          {item?.includedInOriginatorFees && <InOriginationChip />}
        </Flx>
        {percent != null && (
          <Txt sx={{ fontSize: 11, color: grey[500] }}>
            {percent.toFixed(3)}% of loan
          </Txt>
        )}
      </Flx>
      <Box sx={{ textAlign: "right" }}>
        <Txt
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#0f172a",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatDollar(item?.totalDueAmount)}
        </Txt>
      </Box>
      <RowEditHint />
    </Flx>
  );
}

function LedgerThirdPartyRow({ item }) {
  return (
    <Flx ai="flex-start" sx={{ gap: 2 }}>
      <Flx column sx={{ flex: 1, gap: 0.25 }}>
        <Flx ac g={0.75} wrap>
          <Txt sx={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
            {item?.itemName}
          </Txt>
          {item?.serviceType && (
            <Box
              sx={{
                fontSize: 10,
                px: 0.75,
                py: 0.15,
                borderRadius: 0.75,
                background: grey[100],
                color: grey[700],
                textTransform: "capitalize",
              }}
            >
              {String(item.serviceType).replace(/_/g, " ")}
            </Box>
          )}
          {item?.paidOutsideClosing ? <PocChip /> : null}
        </Flx>
        {item?.serviceProviderName && (
          <Txt sx={{ fontSize: 11, color: grey[500] }}>
            {item.serviceProviderName}
          </Txt>
        )}
      </Flx>
      <Box sx={{ textAlign: "right" }}>
        <Txt
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#0f172a",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatDollar(item?.totalDueAmount)}
        </Txt>
      </Box>
      <RowEditHint />
    </Flx>
  );
}

function LedgerEscrowRow({ item, notCollected }) {
  const months = notCollected ? null : getEscrowMonthsCollected(item);
  const subLine = [
    item?.paidTo,
    item?.dueDateFrequency,
    item?.annualAmount ? `${formatDollar(item.annualAmount)}/yr` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Flx ai="flex-start" sx={{ gap: 2 }}>
      <Flx column sx={{ flex: 1, gap: 0.25 }}>
        <Txt sx={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
          {item?.escrowCategory}
          {item?.escrowSubCategory && (
            <Txt component="span" sx={{ color: grey[500] }}>
              {" "}
              · {item.escrowSubCategory}
            </Txt>
          )}
        </Txt>
        {subLine && (
          <Txt sx={{ fontSize: 11, color: grey[500] }}>{subLine}</Txt>
        )}
        {months != null && item?.monthlyAmount != null && (
          <Txt sx={{ fontSize: 11, color: grey[400] }}>
            {months} {months === 1 ? "month" : "months"} ×{" "}
            {formatDollar(item.monthlyAmount)}/mo
          </Txt>
        )}
      </Flx>
      <Box sx={{ textAlign: "right" }}>
        {notCollected ? (
          <Txt sx={{ fontSize: 11, color: grey[500] }}>Not collected</Txt>
        ) : (
          <Txt
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0f172a",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatDollar(getEscrowAmountCollected(item))}
          </Txt>
        )}
      </Box>
      <RowEditHint />
    </Flx>
  );
}

const AutoBadge = () => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.25,
      px: 0.6,
      py: 0.15,
      borderRadius: 0.75,
      background: blue[50],
      color: blue[700],
      border: `1px solid ${blue[100]}`,
      fontSize: 10,
    }}
  >
    <BoltOutlined sx={{ fontSize: 11 }} />
    Auto
  </Box>
);

const PocChip = () => (
  <Txt
    sx={{
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: 0.5,
      color: amber[800],
    }}
  >
    POC
  </Txt>
);

// A fee that duplicates a Holdback & Reserves line — shown, but left out of
// every total on the page (see isHoldbackReserveFee).
const HoldbackDuplicateChip = () => (
  <Tooltip title="The holdback and interest reserve already appear under Holdback & Reserves, so this fee is not counted in Lender Fees, cash to close or the wire on this page.">
    <Box
      sx={{
        fontSize: 10,
        px: 0.75,
        py: 0.15,
        borderRadius: 0.75,
        background: amber[50],
        color: amber[900],
        border: `1px solid ${amber[100]}`,
      }}
    >
      Not counted · in Holdback & Reserves
    </Box>
  </Tooltip>
);

const InOriginationChip = () => (
  <Box
    sx={{
      fontSize: 10,
      px: 0.75,
      py: 0.15,
      borderRadius: 0.75,
      background: grey[100],
      color: grey[700],
    }}
  >
    In Origination
  </Box>
);

const RowEditHint = ({ disabled }) => (
  <Box
    className="row-edit-icon"
    sx={{
      opacity: 0,
      transition: "opacity 0.15s",
      display: "inline-flex",
      alignItems: "center",
    }}
  >
    <Tooltip title={disabled ? "Auto-calculated" : "Click row to edit"}>
      <IconButton size="small" disabled={disabled} sx={{ p: 0.25 }}>
        <EditOutlined sx={{ fontSize: 14, color: grey[500] }} />
      </IconButton>
    </Tooltip>
  </Box>
);

/* ─── Ledger right sidebar: totals, QM, auto-calc ─── */

const LedgerRightSidebar = ({ totals, loanTerms, netWire }) => {
  // Per the LOS model only OriginatorFee/CorrespondentFee can be auto-calculated:
  // the system generates Per Diem Interest + Discount/Origination Points into
  // originatorFees, and Salesforce sync can flag fees in either collection.
  // Credits, broker terms, third-party, and escrow never carry the flag.
  const autoFees = useMemo(
    () =>
      [
        ...(loanTerms?.originatorFees || []),
        ...(loanTerms?.correspondentFees || []),
      ].filter((f) => f?.isAutoCalculated),
    [loanTerms?.originatorFees, loanTerms?.correspondentFees],
  );

  return (
    <Box
      sx={{
        position: { xs: "static", md: "sticky" },
        top: 24,
        alignSelf: "flex-start",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          background: "#ffffff",
          border: `1px solid ${grey[300]}`,
          borderRadius: 2,
          p: 2.5,
        }}
      >
        <Flx ac g={1} sx={{ mb: 2 }}>
          <ReceiptLongOutlined sx={{ fontSize: 16, color: grey[400] }} />
          <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
            Closing Statement Summary
          </Txt>
        </Flx>
        <Flx column g={1}>
          <LedgerLine label="Lender Fees" value={totals.originator} />
          <LedgerLine
            label="Broker (not in orig)"
            value={totals.brokerNotInOrigination}
          />
          <LedgerLine label="Third-Party Fees" value={totals.thirdParty} />
          <LedgerLine
            label="Lender Credits"
            value={-totals.credits}
            negative
          />
          <Box sx={{ borderTop: `1px solid ${grey[300]}`, pt: 1 }}>
            <LedgerLine
              label="Net Closing Fees"
              value={totals.netClosingFees}
              bold
            />
          </Box>
          <LedgerLine label="Escrow Collections" value={totals.escrow} />
          <Box sx={{ borderTop: `2px solid #0f172a`, pt: 1 }}>
            <LedgerLine
              label="Total Cash to Close"
              value={totals.totalCashToClose}
              big
            />
          </Box>
        </Flx>
      </Box>

      <Flx
        column
        g={1.5}
        sx={{
          background: "#ffffff",
          border: `1px solid ${grey[300]}`,
          borderRadius: 2,
          p: 2,
        }}
      >
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
          Auto-Calculated Items
        </Txt>
        {isEmpty(autoFees) ? (
          <Txt sx={{ fontSize: 11, color: grey[500] }}>
            No auto-calculated fees present.
          </Txt>
        ) : (
          <Flx column g={1}>
            {autoFees.map((f) => {
              const detail = autoCalcDetail(f);
              return (
              <Flx ai="flex-start" jb key={f._id || f.oid} g={1}>
                <Flx column g={0.25} sx={{ minWidth: 0 }}>
                  <Flx ac g={0.5}>
                    <BoltOutlined sx={{ fontSize: 11, color: blue[600] }} />
                    <Txt
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: "#334155",
                      }}
                    >
                      {f?.itemName}
                    </Txt>
                  </Flx>
                  {detail && (
                    <Txt sx={{ fontSize: 10, color: grey[500] }}>
                      {detail}
                    </Txt>
                  )}
                </Flx>
                <Txt
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#0f172a",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatDollar(f?.totalDueAmount)}
                </Txt>
              </Flx>
              );
            })}
          </Flx>
        )}
      </Flx>

      <NetWireToTitleCard totals={totals} netWire={netWire} />
      <NetWireToCorrespondentCard totals={totals} loanTerms={loanTerms} />
    </Box>
  );
};

// Net Wire to Title comes from the backend Net Wire Calculator
// (GET /funding_statements/{dealId}/net_wire) — the same figure the Wire &
// Funding tab and the funding statement use. The local estimate only fills in
// while it loads or if it can't be read, and says so.
const NetWireToTitleCard = ({ totals, netWire }) => {
  const fromBackend = !isNil(netWire);
  const lines = fromBackend
    ? [
        {
          label: "Total Loan Amount",
          value: Number(netWire.totalLoanAmount) || totals.loanAmount,
        },
        ...(netWire.withheldLines || []).map((l) => ({
          label: l.label,
          value: -(Number(l.amount) || 0),
          negative: true,
        })),
        {
          label: "Lender Fees",
          value: -sumLineAmounts(netWire.feeLines),
          negative: true,
        },
        {
          label: "Lender Credits",
          value: sumLineAmounts(netWire.creditLines),
          positive: true,
        },
        {
          label: "Escrows",
          value: -sumLineAmounts(netWire.escrowLines),
          negative: true,
        },
      ]
    : [
        { label: "Total Loan Amount", value: totals.loanAmount },
        {
          label: "Holdback & Reserves withheld",
          value: -totals.withheld,
          negative: true,
        },
        { label: "Lender Fees", value: -totals.originator, negative: true },
        { label: "Lender Credits", value: totals.credits, positive: true },
        { label: "Escrows", value: -totals.escrow, negative: true },
      ];
  const wire = fromBackend ? netWire.netWireToTitle : totals.netWireToTitle;
  return (
    <Box
      sx={{
        background: "#ffffff",
        border: `1px solid ${grey[300]}`,
        borderRadius: 2,
        p: 2.5,
      }}
    >
      <Flx ac g={1} sx={{ mb: 1.5 }}>
        <PaidOutlined sx={{ fontSize: 16, color: grey[400] }} />
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
          Net Wire to Title
        </Txt>
      </Flx>
      <Flx column g={1}>
        {lines.map((l) => (
          <Flx ac jb key={l.label}>
            <Txt sx={{ fontSize: 12, color: grey[700] }}>{l.label}</Txt>
            <Txt
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: l.negative
                  ? "#b91c1c"
                  : l.positive
                    ? green[600]
                    : "#0f172a",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {l.negative ? "-" : l.positive ? "+" : ""}
              {formatDollar(Math.abs(l.value))}
            </Txt>
          </Flx>
        ))}
        <Box sx={{ borderTop: `2px solid #0f172a`, pt: 1 }}>
          <Flx ac jb>
            <Txt sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
              Net Wire to Title
            </Txt>
            <Txt
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0f172a",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatDollar(wire)}
            </Txt>
          </Flx>
          <Txt component="div" sx={{ fontSize: 10.5, color: grey[500], mt: 0.5 }}>
            {fromBackend
              ? "From the funding calculation, the same figure as Wire & Funding."
              : netWire === null
                ? "Estimate: the funding calculation couldn't be loaded."
                : "Estimate: loading the funding calculation…"}
          </Txt>
        </Box>
      </Flx>
    </Box>
  );
};

const NetWireToCorrespondentCard = ({ totals, loanTerms }) => {
  const evChargedItems = useMemo(
    () =>
      (loanTerms?.correspondentFees || []).filter(isEvChargedCorrespondentFee),
    [loanTerms?.correspondentFees],
  );

  // Nothing to wire and nothing charged → hide the card entirely.
  if (
    totals.correspondent === 0 &&
    totals.correspondentEvCharged === 0
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        background: "#ffffff",
        border: `1px solid ${grey[300]}`,
        borderRadius: 2,
        p: 2.5,
      }}
    >
      <Flx ac g={1} sx={{ mb: 1.5 }}>
        <PeopleOutlined sx={{ fontSize: 16, color: grey[400] }} />
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
          Net Wire to Correspondent
        </Txt>
      </Flx>
      <Flx column g={1}>
        <Flx ac jb>
          <Txt sx={{ fontSize: 12, color: grey[700] }}>
            Fees paid to correspondent
          </Txt>
          <Txt
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: "#0f172a",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatDollar(totals.correspondentPaidOut)}
          </Txt>
        </Flx>
        <Flx ac jb>
          <Txt sx={{ fontSize: 12, color: grey[700] }}>
            Less fees EV charges
          </Txt>
          <Txt
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: "#b91c1c",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            -{formatDollar(totals.correspondentEvCharged)}
          </Txt>
        </Flx>
        {evChargedItems.length > 0 && (
          <Flx column g={0.25} sx={{ pl: 1.5 }}>
            {evChargedItems.map((f) => (
              <Flx ac jb key={f._id || f.oid || f.itemName}>
                <Txt sx={{ fontSize: 10.5, color: grey[500] }}>
                  {f?.itemName || f?.feeCategory}
                </Txt>
                <Txt
                  sx={{
                    fontSize: 10.5,
                    color: grey[500],
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  -{formatDollar(f?.totalDueAmount)}
                </Txt>
              </Flx>
            ))}
          </Flx>
        )}
        <Box sx={{ borderTop: `2px solid #0f172a`, pt: 1 }}>
          <Flx ac jb>
            <Txt sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
              Net Wire to Correspondent
            </Txt>
            <Txt
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0f172a",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatDollar(totals.netWireToCorrespondent)}
            </Txt>
          </Flx>
        </Box>
      </Flx>
    </Box>
  );
};

const LedgerLine = ({ label, value, bold, big, negative }) => (
  <Flx ac jb>
    <Txt
      sx={{
        fontSize: big ? 14 : bold ? 12 : 12,
        fontWeight: big ? 600 : bold ? 500 : 400,
        color: big || bold ? "#0f172a" : grey[700],
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontSize: big ? 18 : bold ? 13 : 12,
        fontWeight: big ? 700 : bold ? 600 : 400,
        color: big ? "#0f172a" : negative ? green[600] : "#0f172a",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {formatDollar(value)}
    </Txt>
  </Flx>
);

export default LoanDrilldownFeesRedesignTab;
